import React, { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  assignAgentToCustomer,
  getCustomerList,
} from "rc-app/api/customerActionsApi";
import {
  getClientAdminByEmailId,
  getClientManagerByEmailId,
  getSystemAdminList,
  getBusinessAdminList,
} from "rc-app/api/systemBusinessAdminActionApi";
import { getAgencyById } from "rc-app/api/agencyActionsApi";
import { getClientProjects } from "rc-app/api/projectActionsApi";
import {
  getAgentById,
  getAgentProfile,
  getAllAgent,
} from "rc-app/api/agentActionsApi";
import UserService from "rc-app/services/UserService";
import Icon from "rc-app/components/Icons";
import { toTitleCase } from "rc-app/utils";
import AssignAgentModal from "./AssignAgentModal";
import { toast } from "react-toastify";
import { AddButton } from "rc-app/components/Buttons";
import { BadgeCheck } from "lucide-react";
import {
  ORIGIN_OPTIONS,
  originMeta,
  matchesOriginFilter,
  isEmbedOrigin,
} from "rc-app/utils/leadOrigin";

interface Lead {
  id: string;
  status: "hot" | "warm" | "cold" | "customer" | "denied" | "";
  fullName: string;
  mobile: string;
  verified: boolean;
  email: string;
  createdAt?: string;
  createdAtRaw?: string;
  lastUpdate?: string;
  interestedIn?: string;
  city?: string;
  profileStatus?: string;
  linkedIn?: string;
  addressLine1?: string;
  addressLine2?: string;
  landmark?: string;
  pincode?: string;
  state?: string;
  referredBy?: string;
  investmentType?: string;
  annualIncome?: string;
  budget?: string;
  occupationType?: string;
  incomeSource?: string;
  familySize?: string;
  agentIds?: string[];
  // Lead origin (Task 9): who created a platform lead.
  originSource?: string;
  actorUserId?: string;
  actorRole?: string;
  // Share-origin: the sharer + the project the share link was for.
  referrerUserId?: string;
  referrerProjectId?: string;
  // Embed-origin: the builder website the lead came through.
  embedDomain?: string;
}

interface LeadListProps {
  onMoreInfo: (lead: Lead) => void;
}

/* Origin labels, pill styles and the Origin filter live in utils/leadOrigin —
   including why "embed" and "sdk" both read as Embed. */

export const LeadList: React.FC<LeadListProps> = ({ onMoreInfo }) => {
  const navigate = useNavigate();
  const { slug: agencySlug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<"leads" | "unassigned">("leads");

  // const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const [agencyId, setAgencyId] = useState<string>("");

  const [agentId, setAgentId] = useState<string>("");
  const [agentNames, setAgentNames] = useState<
    Record<string, { name: string; agentId: string }>
  >({});

  // Agency staff directory (agent + asa + aba) keyed by _id → resolves a
  // platform lead's origin.actor_user_id into a creator name + role.
  const [actorDirectory, setActorDirectory] = useState<
    Record<string, { name: string; role: string }>
  >({});
  // Agency project id → name, to resolve a share lead's referrer_project_id.
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});

  const [assignedLeads, setAssignedLeads] = useState<Lead[]>([]);
  const [unassignedLeads, setUnassignedLeads] = useState<Lead[]>([]);
  const isAgent = UserService.hasRole("agent");
  const isABA =
    UserService.hasRole("agency_business_admin") ||
    UserService.hasRole("super_admin");
  // ASA-only (not also ABA/super). ASAs see only their team's leads and cannot
  // assign — lead distribution is an ABA-only lever (decided 2026-06-16).
  const isASA = UserService.hasRole("agency_system_admin") && !isABA;
  const [selfAsaId, setSelfAsaId] = useState<string>("");
  // Keycloak ids of the logged-in agent / ASA — needed to match SHARE leads,
  // whose referrer_user_id is a keycloak UUID (not the Mongo _id).
  const [agentKeycloakId, setAgentKeycloakId] = useState<string>("");
  const [selfAsaKeycloakId, setSelfAsaKeycloakId] = useState<string>("");
  // This ASA's own team of agents (id + display name) — used to scope leads,
  // gate lead creation, and populate the agent dropdown on the create form.
  const [asaAgents, setAsaAgents] = useState<{ id: string; name: string }[]>(
    [],
  );

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");

  const [recentlyAssignedId, setRecentlyAssignedId] = useState<string>("");

  const PER_PAGE = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  // Origin filter: "" = all, else one of the source enum values.
  const [originFilter, setOriginFilter] = useState<string>("");

  useEffect(() => {
    if (UserService.hasRole("agent")) {
      getAgentProfile(UserService.getUseremail()).then((res) => {
        setAgentId(res?.agent?._id);
        setAgentKeycloakId(res?.agent?.keycloak_user_id || "");
        getAgencyById(res?.agent?.agency["id"]).then((res) => {
          setAgencyId(res?._id);
        });
      });
    }
    if (UserService.hasRole("agency_business_admin")) {
      getClientManagerByEmailId(UserService.getUseremail()).then((res) => {
        setAgencyId(res?.agency_business_admin?.agency_id);
      });
    }
    if (UserService.hasRole("agency_system_admin")) {
      getClientAdminByEmailId(UserService.getUseremail()).then((res) => {
        setAgencyId(res?.agency_system_admin?.agency_id);
        setSelfAsaId(res?.agency_system_admin?._id || "");
        setSelfAsaKeycloakId(res?.agency_system_admin?.keycloak_id || "");
      });
    }
  }, []);

  useEffect(() => {
    if (isAgent && agentId) fetchAgentLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agentKeycloakId]);

  useEffect(() => {
    if (!isAgent && agencyId) fetchAdminLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId, selfAsaId, selfAsaKeycloakId]);

  // Build the agency staff directory once we know the agency. Each list call is
  // independent + fail-soft (an agent may lack permission on the admin lists —
  // that just leaves those names unresolved; we fall back to the role label).
  useEffect(() => {
    if (!agencyId) return;
    (async () => {
      // Admin (asa/aba) records carry a single `name`; agents carry
      // first/last. Prefer whichever exists; email only as a last resort.
      const nm = (o: any) =>
        (o?.name && String(o.name).trim()) ||
        `${o?.first_name || ""} ${o?.last_name || ""}`.trim() ||
        o?.email_id ||
        "";
      const [agents, asas, abas, projects] = await Promise.all([
        getAllAgent(0, 10000, agencyId as any).catch(() => null),
        getSystemAdminList(agencyId).catch(() => null),
        getBusinessAdminList(agencyId).catch(() => null),
        getClientProjects(agencyId).catch(() => null),
      ]);
      const dir: Record<string, { name: string; role: string }> = {};
      // Index each staff member by BOTH their Mongo _id AND keycloak_user_id, so
      // a referrer_user_id / actor_user_id resolves whichever form the backend
      // stored (the share link stamps the sharer's keycloak id, not the _id).
      const add = (a: any, role: string) => {
        const entry = { name: nm(a), role };
        if (a?._id) dir[a._id] = entry;
        // Agents carry `keycloak_user_id`; ABA/ASA carry `keycloak_id`. The
        // share link stamps the sharer's keycloak UUID, so index both.
        if (a?.keycloak_user_id) dir[a.keycloak_user_id] = entry;
        if (a?.keycloak_id) dir[a.keycloak_id] = entry;
      };
      (agents?.re_agent_list || []).forEach((a: any) => add(a, "agent"));
      (asas?.agency_system_admin_list || []).forEach((a: any) => add(a, "asa"));
      (abas?.agency_business_admin_list || []).forEach((a: any) => add(a, "aba"));
      setActorDirectory(dir);
      const pmap: Record<string, string> = {};
      (projects?.projects || []).forEach((p: any) => {
        if (p?._id)
          pmap[p._id] =
            p?.project_name || p?.name || `Project ${String(p._id).slice(-4)}`;
      });
      setProjectNames(pmap);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const formatDate = (iso: string) => {
    if (!iso) return "-";
    // Backend stamps are UTC; a naive (no-timezone) string is treated as UTC,
    // then rendered in IST (GMT+5:30) regardless of the viewer's browser zone.
    const norm = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`;
    const d = new Date(norm);
    if (isNaN(d.getTime())) return "-";
    return d
      .toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const mapCustomerToLead = (customer: any): Lead => ({
    id: customer._id,
    status: "",
    fullName: `${customer.first_name} ${customer.last_name}`,
    mobile: customer.mobile_number,
    verified: !!customer.keycloak_user_id,
    email: customer.email_id,
    createdAt: customer.created_at ? formatDate(customer.created_at) : "-",
    createdAtRaw: customer.created_at || "",
    interestedIn: customer.interested_in?.join(", ") || "",
    city: customer.address?.city || "",
    profileStatus: customer.is_active ? "Active" : "Inactive",
    linkedIn: customer.linkedin_profile || "",
    addressLine1: customer.address?.address_line1 || "",
    addressLine2: customer.address?.address_line2 || "",
    landmark: customer.address?.landmark || "",
    pincode: customer.address?.postal_code || "",
    state: customer.address?.province || "",
    referredBy: customer.referred_by_mobile_number || "",
    investmentType: customer.investment_type || "",
    annualIncome: customer.annual_income || "",
    budget: customer.budget || "",
    occupationType: customer.occupation_type || "",
    incomeSource: customer.income_source || "",
    familySize: customer.family_size?.toString() || "",
    agentIds: customer.agent_id || [],
    originSource: customer.origin?.source || "",
    actorUserId: customer.origin?.actor_user_id || "",
    actorRole: customer.origin?.actor_role || "",
    referrerUserId: customer.referrer_user_id || "",
    referrerProjectId: customer.referrer_project_id || "",
    embedDomain: customer.embed_domain || "",
  });

  // Agent: leads ASSIGNED to them (reliable server filter) UNION leads they
  // CREATED (origin.actor_user_id) or SHARED (referrer_user_id). The latter two
  // need the full agency list — best-effort, since an agent token may not be
  // allowed to read it; if that call is denied they still see assigned leads.
  const fetchAgentLeads = async () => {
    try {
      setLoading(true);
      const [assignedRes, allRes] = await Promise.all([
        getCustomerList(0, 10000, agencyId, agentId).catch(() => null),
        getCustomerList(0, 10000, agencyId).catch(() => null),
      ]);
      const owner = new Set(
        [agentId, agentKeycloakId].filter(Boolean) as string[],
      );
      const createdOrShared = ((allRes as any)?.re_customer_list || []).filter(
        (c: any) =>
          owner.has(c?.origin?.actor_user_id) || owner.has(c?.referrer_user_id),
      );
      const byId = new Map<string, any>();
      [
        ...((assignedRes as any)?.re_customer_list || []),
        ...createdOrShared,
      ].forEach((c: any) => {
        if (c?._id) byId.set(c._id, c);
      });
      setAssignedLeads(Array.from(byId.values()).map(mapCustomerToLead));
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Admin: fetch all agency leads, split into assigned/unassigned.
  // ABA sees the whole agency (both tabs + assign). An ASA sees ONLY their
  // team's assigned leads (no unassigned pool, no assign) — scoped to the
  // agents assigned to them. Until agents carry `assigned_asa_id` we can't
  // identify the team, so we fall back to all assigned leads (mirrors the
  // Agents tab fallback) rather than showing an empty list.
  const fetchAdminLeads = async () => {
    try {
      setLoading(true);
      const response = await getCustomerList(0, 10000, agencyId);
      const allLeads = response.re_customer_list;

      const hasValidAgent = (c: any) =>
        c.agent_id &&
        c.agent_id.length > 0 &&
        c.agent_id.some((id: string) => id && id.trim() !== "");

      let assigned = allLeads.filter((c: any) => hasValidAgent(c));
      let unassigned = allLeads.filter((c: any) => !hasValidAgent(c));

      if (isASA) {
        // Resolve this ASA's downline agents (assigned_asa_id === this ASA).
        const agentsRes: any = await getAllAgent(
          0,
          10000,
          agencyId as any,
        ).catch(() => null);
        const teamRaw = (agentsRes?.re_agent_list || []).filter((ag: any) => {
          const raw = ag.assigned_asa_id || ag.asa_id;
          const id = raw ? String(Array.isArray(raw) ? raw[0] : raw) : "";
          return id && selfAsaId && id === selfAsaId;
        });
        const team = teamRaw.map((ag: any) => ({
          id: ag._id,
          name:
            `${ag.first_name || ""} ${ag.last_name || ""}`.trim() ||
            ag.email_id ||
            "Agent",
        }));
        setAsaAgents(team);
        const teamAgentIds = new Set<string>(team.map((t: any) => t.id));
        // Owner set = the ASA + downline agents, in BOTH _id and keycloak forms,
        // so we can match created (actor_user_id = _id) and shared
        // (referrer_user_id = keycloak UUID) leads.
        const owner = new Set<string>(
          [selfAsaId, selfAsaKeycloakId].filter(Boolean),
        );
        teamRaw.forEach((ag: any) => {
          if (ag?._id) owner.add(ag._id);
          if (ag?.keycloak_user_id) owner.add(ag.keycloak_user_id);
        });
        // ASA sees: assigned to their team OR created/shared by ASA/team.
        // Single list (no unassigned tab), so everything scoped goes to
        // `assigned`.
        assigned = allLeads.filter(
          (c: any) =>
            (c.agent_id || []).some((id: string) => teamAgentIds.has(id)) ||
            owner.has(c?.origin?.actor_user_id) ||
            owner.has(c?.referrer_user_id),
        );
        unassigned = [];
      }

      setAssignedLeads(assigned.map(mapCustomerToLead));
      setUnassignedLeads(unassigned.map(mapCustomerToLead));

      const uniqueAgentIds = [
        ...new Set(
          assigned.flatMap((c: any) =>
            (c.agent_id || []).filter((id: string) => id && id.trim() !== ""),
          ),
        ),
      ] as string[];

      const agentMap: Record<string, { name: string; agentId: string }> = { ...agentNames };
      const newAgentIds = uniqueAgentIds.filter((id) => !agentMap[id]);
      if (newAgentIds.length > 0) {
        await Promise.all(
          newAgentIds.map(async (id) => {
            try {
              const res = await getAgentById(id);
              agentMap[id] = {
                name: `${res?.agent?.first_name || ""} ${res?.agent?.last_name || ""}`.trim(),
                agentId: res?.agent?.agent_id || "",
              };
            } catch {
              agentMap[id] = { name: "Unknown", agentId: "" };
            }
          }),
        );
      }
      setAgentNames(agentMap);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };


  const roleLabel = (r?: string) =>
    r === "agent"
      ? "Agent"
      : r === "asa"
        ? "System Admin"
        : r === "aba"
          ? "Business Admin"
          : "";

  // Created-By cell content, per origin source. Platform → creator; share →
  // "Via {sharer} · Project {P}"; anything else → dash.
  /* Guided rooms resolve the same way as platform: origin.actor_user_id is
     whoever CREATED the lead, and for a guided room that is the agent who
     hosted the tour — exactly what a business admin wants against a lead
     that appeared out of a live walkthrough.

     If a guided row still shows a dash, the backend isn't stamping
     origin.actor_user_id when verify_otp creates the customer. The field is
     already on RECustomer and the mapper above already reads it for every
     origin, so nothing here changes when it starts arriving. */
  const createdByContent = (lead: Lead) => {
    const src = lead.originSource;
    if (src === "platform" || src === "guided_room") {
      const name = actorDirectory[lead.actorUserId || ""]?.name;
      const rl = roleLabel(lead.actorRole);
      /* A guided lead names its project the same way a share lead does.
         verify_otp stamps referrer_project_id from the ROOM, so the id has
         always been there — this column simply wasn't reading it, and
         "Business Admin" on its own tells you nothing about which tour the
         lead walked out of. A platform lead has no project by definition
         (someone typed it into the CRM), so the line stays absent there
         rather than being faked. */
      const proj = src === "guided_room"
        ? projectNames[lead.referrerProjectId || ""]
        : "";
      const sub = [rl, proj ? `Project ${proj}` : ""].filter(Boolean).join(" · ");
      return name ? (
        <div>
          <span className="text-white font-medium">
            {toTitleCase(name, " ")}
          </span>
          {sub && <div className="text-[#7C97A8] text-xs">{sub}</div>}
        </div>
      ) : (
        <div>
          <span className="text-[#C7D6DD]">{rl || "—"}</span>
          {proj && <div className="text-[#7C97A8] text-xs">Project {proj}</div>}
        </div>
      );
    }
    if (src === "share_page" || src === "share_otp") {
      const entry = actorDirectory[lead.referrerUserId || ""];
      const name = entry?.name;
      const proj = projectNames[lead.referrerProjectId || ""];
      const sub = [roleLabel(entry?.role), proj ? `Project ${proj}` : ""]
        .filter(Boolean)
        .join(" · ");
      return name ? (
        <div>
          <span className="text-white font-medium">
            {toTitleCase(name, " ")}
          </span>
          {sub && <div className="text-[#7C97A8] text-xs">{sub}</div>}
        </div>
      ) : (
        <div>
          <span className="text-[#C7D6DD]">Shared link</span>
          {proj && <div className="text-[#7C97A8] text-xs">Project {proj}</div>}
        </div>
      );
    }
    /* An embed lead has no person behind it — nobody created or shared it. What
       a business admin wants instead is WHICH of their websites produced it, and
       the backend records that on the lead at creation. */
    if (isEmbedOrigin(src)) {
      return lead.embedDomain ? (
        <div>
          <span className="text-white font-medium">{lead.embedDomain}</span>
          <div className="text-[#7C97A8] text-xs">Website embed</div>
        </div>
      ) : (
        <span className="text-[#C7D6DD]">Website embed</span>
      );
    }
    return <span className="text-[#7C97A8]">—</span>;
  };

  const initials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "NA";

  const matchesSearch = (lead: Lead) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      lead.fullName.toLowerCase().includes(q) ||
      (lead.mobile || "").toLowerCase().includes(q) ||
      (lead.email || "").toLowerCase().includes(q)
    );
  };

  const handleAssign = async (agentMongoId: string) => {
    try {
      await assignAgentToCustomer(selectedLeadId, agentMongoId);
      toast.success("Agent assigned successfully");
      setShowAssignModal(false);
      setSelectedLeadId("");
      setRecentlyAssignedId(selectedLeadId);
      setTimeout(() => setRecentlyAssignedId(""), 7000);
      setActiveTab("leads");
      fetchAdminLeads();
    } catch (error) {
      toast.error("Failed to assign agent");
    }
  };


  // Agent-Associated column (ABA + ASA). The assign action lives INSIDE it:
  // agent name when assigned, an Assign button when not. Both ABA and ASA can
  // assign (ASA scoped to their own agents via the modal's allowedAgentIds).
  const showAgentCol = !isAgent;
  const colCount = 8 + (showAgentCol ? 1 : 0);
  const sourceLeads = isAgent
    ? assignedLeads
    : activeTab === "leads"
      ? assignedLeads
      : unassignedLeads;
  const filteredLeads = sourceLeads
    .filter(matchesSearch)
    .filter((l) => matchesOriginFilter(l.originSource, originFilter))
    // Newest first — the agent fetch merges lists, so sort client-side.
    .sort((a, b) => {
      const ta = a.createdAtRaw ? new Date(a.createdAtRaw).getTime() : 0;
      const tb = b.createdAtRaw ? new Date(b.createdAtRaw).getTime() : 0;
      return tb - ta;
    });
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const pageLeads = filteredLeads.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // An ASA can only create a lead if they have at least one agent to attach it
  // to. ABA/agent can always create.
  const asaHasAgents = asaAgents.length > 0;
  const canAddLead = isASA ? asaHasAgents : true;

  // Empty-state copy + whether to offer an "Add New Lead" CTA, per context.
  const leadEmpty = search
    ? {
        title: "No matching leads",
        subtitle: "Try a different name, mobile number, or email.",
        showAdd: false,
      }
    : isASA && !asaHasAgents
      ? {
          title: "No agents assigned to you yet",
          subtitle:
            "You don't have any agents yet, so there are no leads to manage. Ask your Business Admin to assign agents to you to get started.",
          showAdd: false,
        }
      : isASA
        ? {
            title: "No leads yet",
            subtitle:
              "You don't have any leads yet. Add a new lead and assign it to one of your agents.",
            showAdd: true,
          }
        : activeTab === "unassigned"
          ? {
              title: "No unassigned leads",
              subtitle: "Every lead is currently assigned to an agent.",
              showAdd: false,
            }
          : {
              title: "No leads yet",
              subtitle: "Leads will appear here once they're added.",
              showAdd: false,
            };

  return (
    <div className="font-inter h-full text-white">
      {/* Breadcrumb + search + add */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => navigate(agencySlug ? `/c/${agencySlug}` : "/")}
            className="text-[#889DB2] hover:underline"
          >
            Home
          </button>
          <span className="text-[#889DB2] mx-1">›</span>
          <span className="text-white font-medium">
            My Leads / Customers (
            {assignedLeads.length + unassignedLeads.length})
          </span>
        </div>
        <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 sm:pl-12 w-full sm:w-64 lg:w-85 px-3 py-2 text-sm sm:text-base text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors placeholder:text-[#889DB2]"
            />
            <Icon
              name="ic-search"
              className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-4 h-4"
            />
          </div>
          <div className="w-full sm:w-52">
            <Listbox
              value={originFilter}
              onChange={(v: string) => {
                setOriginFilter(v);
                setCurrentPage(1);
              }}
            >
              <div className="relative">
                <Listbox.Button className="w-full bg-[#1F3D48] border border-[#3D5468] text-sm rounded-lg px-3.5 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-[#008564]/30 focus:border-[#008564]/60 cursor-pointer hover:bg-[#243F4C] hover:border-[#5A7088] transition-all text-left flex items-center gap-2">
                  <span className="flex-1 truncate text-white">
                    {ORIGIN_OPTIONS.find((o) => o.value === originFilter)
                      ?.label || "All Origins"}
                  </span>
                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#889DB2]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-[#15333D] border border-[#2C4D5A] py-1 shadow-2xl focus:outline-none">
                    {ORIGIN_OPTIONS.map((o) => (
                      <Listbox.Option
                        key={o.value}
                        value={o.value}
                        className={({ active, selected }) =>
                          `cursor-pointer select-none px-3.5 py-2 text-sm flex items-center gap-2 transition-colors ${
                            active ? "bg-white/5" : ""
                          } ${selected ? "bg-[#008564]/10" : ""}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`flex-1 truncate ${
                                selected
                                  ? "text-white font-medium"
                                  : "text-[#E5E7EB]"
                              }`}
                            >
                              {o.label}
                            </span>
                            {selected && (
                              <svg
                                className="w-3.5 h-3.5 text-[#34D399] flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.5}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
          {canAddLead && (
            <AddButton
              label="Add New Lead"
              navigateTo="/add_lead"
              iconClassName="h-3 w-3"
            />
          )}
        </div>
      </div>

      {/* Assigned / Unassigned tabs — ABA only. An ASA sees a single list of
          their team's leads; an agent sees only their own. */}
      {isABA && (
        <div className="mb-4 flex items-center gap-2">
          {(
            [
              {
                key: "leads",
                label: "Assigned Leads",
                count: assignedLeads.length,
              },
              {
                key: "unassigned",
                label: "Unassigned Leads",
                count: unassignedLeads.length,
              },
            ] as const
          ).map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setActiveTab(t.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-[#34D399] text-[#34D399]"
                    : "border-[#2C4D5A] text-[#9FB8AF] hover:text-white hover:border-[#3B5C6A]"
                }`}
              >
                {t.label}
                <span
                  className={`inline-flex min-w-[20px] h-5 items-center justify-center rounded-full px-1.5 text-xs ${
                    active
                      ? "bg-[#34D399] text-[#0B2027]"
                      : "bg-white/10 text-[#9FB8AF]"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Row table */}
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1160px] border-collapse text-left">
            <thead>
              <tr className="bg-[#11272E] text-[11px] uppercase tracking-wide text-[#7C97A8]">
                <th className="px-4 py-3 font-medium">Full Name</th>
                <th className="px-4 py-3 font-medium">Mobile Number</th>
                <th className="px-4 py-3 font-medium">Email ID</th>
                {showAgentCol && (
                  <th className="px-4 py-3 font-medium">Agent</th>
                )}
                <th className="px-4 py-3 font-medium">Origin</th>
                <th className="px-4 py-3 font-medium">Created By</th>
                <th className="px-4 py-3 font-medium">Creation Date</th>
                <th className="px-4 py-3 font-medium text-center">Details</th>
                <th className="px-4 py-3 font-medium text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colCount} className="py-16 text-center">
                    <div className="inline-block w-10 h-10 border-4 border-[#34D399] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : pageLeads.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="py-14">
                    <div className="flex flex-col items-center justify-center text-center px-6">
                      <div className="w-20 h-20 mb-4 rounded-full bg-[#11272E] border border-[#2C4D5A] flex items-center justify-center">
                        <Icon
                          name="ic-client-customers"
                          className="w-11 h-11 opacity-90"
                        />
                      </div>
                      <h3 className="text-white text-base font-semibold mb-1.5">
                        {leadEmpty.title}
                      </h3>
                      <p className="text-[#7C97A8] text-sm max-w-md leading-relaxed">
                        {leadEmpty.subtitle}
                      </p>
                      {leadEmpty.showAdd && (
                        <button
                          type="button"
                          onClick={() => navigate("/add_lead")}
                          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#008564] hover:bg-[#0a9b76] text-white text-sm font-medium px-4 py-2 transition-colors"
                        >
                          <Icon name="ic-plus" className="w-3.5 h-3.5" />
                          Add New Lead
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                pageLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className={`border-t border-[#22323D] hover:bg-[#1A2F38] transition-colors ${
                      lead.id === recentlyAssignedId ? "bg-[#143a2c]" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#34D399] to-[#059669] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {initials(lead.fullName)}
                        </div>
                        <span className="text-sm text-white font-medium whitespace-nowrap">
                          {toTitleCase(lead.fullName, " ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {lead.mobile}
                        {lead.verified && (
                          <BadgeCheck
                            className="w-4 h-4 text-[#34D399]"
                            aria-label="Verified"
                          />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      {lead.email}
                    </td>
                    {showAgentCol && (
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {(lead.agentIds || []).some(
                          (id) => id && id.trim() !== "",
                        ) ? (
                          (lead.agentIds || [])
                            .filter((id) => id && id.trim() !== "")
                            .map((id) => (
                              <div key={id} className="text-sm">
                                <span className="text-white font-medium">
                                  {agentNames[id]?.name
                                    ? toTitleCase(agentNames[id].name, " ")
                                    : "Loading…"}
                                </span>
                                {agentNames[id]?.agentId && (
                                  <span className="text-[#7C97A8] ml-1">
                                    ({agentNames[id].agentId})
                                  </span>
                                )}
                              </div>
                            ))
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedLeadId(lead.id);
                              setShowAssignModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-md bg-[#008564] hover:bg-[#0a9b76] text-white text-xs font-medium px-3 py-1.5 transition-colors"
                          >
                            Assign
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${originMeta(lead.originSource).cls}`}
                      >
                        {originMeta(lead.originSource).label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {createdByContent(lead)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#9FB8AF] whitespace-nowrap">
                      {lead.createdAt || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => onMoreInfo(lead)}
                        className="text-xs text-[#7DB6FB] hover:text-[#a8cefc] underline whitespace-nowrap"
                      >
                        View
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() =>
                          navigate(`/edit_lead/${lead.id}`, { state: { lead } })
                        }
                        title="Edit lead"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#9FB8AF] hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <Icon name="ic-edit" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-md bg-[#2E4051] text-white text-sm disabled:opacity-40 hover:bg-[#3a5063] transition-colors"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                p === page
                  ? "bg-[#008564] text-white"
                  : "bg-[#2E4051] text-white hover:bg-[#3a5063]"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-md bg-[#2E4051] text-white text-sm disabled:opacity-40 hover:bg-[#3a5063] transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {showAssignModal && (
        <AssignAgentModal
          agencyId={agencyId}
          allowedAgentIds={isASA ? asaAgents.map((a) => a.id) : undefined}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedLeadId("");
          }}
          onAssign={handleAssign}
        />
      )}
    </div>
  );
};
